import axios from "axios";

// Arma el desglose de una orden de compra para el PDF y para el Excel:
// un renglon por articulo con el total de piezas y, debajo, como se reparten
// entre sus colores y tallas. Tambien trae la foto del modelo ya resuelta.
//
// Las fotos se bajan ANTES de armar el documento (Promise.all): si se mandan a
// jsPDF sin esperar, addImage truena con "UNKNOWN".
const headers = () => ({ Authorization: `Bearer: ${localStorage.getItem("app_token")}` });
const num = (n) => new Intl.NumberFormat("en-US").format(Number(n) || 0);
const nombreColor = (c) => (c ? (c.name) : "Sin color");

// De las cantidades sale la proporcion: 300/600/600/300 -> "1-2-2-1".
// Si no se puede reducir a enteros chicos se deja la cantidad tal cual.
const mcd = (a, b) => (b ? mcd(b, a % b) : a);
export function proporcionDe(cantidades) {
  const crudas = cantidades.map((n) => Math.round(Number(n) || 0));
  const ns = crudas.filter((n) => n > 0);
  if (!ns.length) return "";
  // 1) divisor comun exacto: 300/600/600/300 -> 1-2-2-1
  const divisor = ns.reduce((d, n) => mcd(d, n), ns[0]) || 1;
  const exactas = crudas.map((n) => Math.round(n / divisor));
  if (Math.max(...exactas) <= 99) return exactas.join("-");
  // 2) si el reparto dejo residuos (133/267/267/133) no hay divisor comun:
  //    se aproxima contra la cantidad mas chica y se acepta si todas caen
  //    cerca de un entero.
  const minimo = Math.min(...ns);
  const aprox = crudas.map((n) => n / minimo);
  const cerca = aprox.every((x) => Math.abs(x - Math.round(x)) <= 0.12);
  const redondeadas = aprox.map((x) => Math.round(x));
  if (cerca && Math.max(...redondeadas) <= 99) return redondeadas.join("-");
  // 3) ni modo: las cantidades tal cual
  return crudas.join("-");
}

export async function fotoDeArticulo(id) {
  try {
    const r = await axios.get(`${process.env.REACT_APP_URL_GETMEDIA}/${id}`, { headers: headers() });
    const url = r.data?.secure_url;
    if (!url) return null;
    const img = await fetch(url).then((x) => (x.ok ? x.blob() : null));
    if (!img) return null;
    return await new Promise((resolve) => {
      const fr = new FileReader();
      fr.onloadend = () => resolve(typeof fr.result === "string" ? fr.result : null);
      fr.onerror = () => resolve(null);
      fr.readAsDataURL(img);
    });
  } catch (e) {
    return null;
  }
}

// Devuelve [{ articulos, codigo, nombre, foto, piezas, precio, total, variantes:[{color,talla,cantidad}], desglose }]
export async function desgloseOrdenCompra(idOrden, { conFoto = true } = {}) {
  const r = await axios.get(`${process.env.REACT_APP_URL_ARTICULOS_OC}/ordenesCompra/${idOrden}`, { headers: headers() });
  const lineas = (r.data || []).filter((a) => a.articulos?.[0]);
  const porArticulo = new Map();
  lineas.forEach((a) => {
    const art = a.articulos[0];
    // modelo + renglon: el mismo modelo en dos renglones sale en dos renglones
    const k = `${art._id}|${a.renglon || 1}`;
    if (!porArticulo.has(k)) {
      porArticulo.set(k, {
        articulos: String(art._id), renglon: a.renglon || 1, codigo: art.codigo || "", nombre: art.nombre || "",
        precio: Number(a.precio) || 0, piezas: 0, total: 0, foto: null, variantes: [],
      });
    }
    const x = porArticulo.get(k);
    const cantidad = Number(a.cantidad) || 0;
    x.piezas += cantidad;
    x.total += Number(a.total) || cantidad * (Number(a.precio) || 0);
    const col = a.colores?.[0];
    x.variantes.push({
      idColor: col?._id || "",
      colorNombre: col?.name || "Sin color",
      color: nombreColor(col),
      talla: a.tallas?.[0]?.name || "Sin talla",
      cantidad,
    });
  });

  const articulos = [...porArticulo.values()];
  // El documento va para el proveedor: los colores en una columna, las tallas
  // en otra, y al lado cuantas piezas lleva cada uno (la proporcion).
  articulos.forEach((a) => {
    const porColor = {}, porTalla = {};
    a.variantes.forEach((v) => {
      porColor[v.color] = (porColor[v.color] || 0) + v.cantidad;
      porTalla[v.talla] = (porTalla[v.talla] || 0) + v.cantidad;
    });
    a.porColor = porColor;
    a.porTalla = porTalla;
    a.colores = Object.keys(porColor);
    a.tallas = Object.keys(porTalla);
    // columnas alineadas: un renglon por color y un renglon por talla
    a.colColores = a.colores.join("\n");
    a.colPiezasColor = a.colores.map((c) => num(porColor[c])).join("\n");
    a.colTallas = a.tallas.join("\n");
    a.colPiezasTalla = a.tallas.map((t) => num(porTalla[t])).join("\n");
    // la proporcion como la escribe el cliente: 1-2-2-1
    a.proporcionTallas = proporcionDe(a.tallas.map((t) => porTalla[t]));
    a.proporcionColores = proporcionDe(a.colores.map((c) => porColor[c]));
    // de una linea, para el Excel y para cuando se quiera leer corrido
    a.desglose = Object.entries(porColor).map(([c, q]) => `${c} ${num(q)}`).join(" | ");
    a.desgloseTallas = Object.entries(porTalla).map(([t, q]) => `${t} ${num(q)}`).join(" | ");
  });

  if (conFoto) {
    const fotos = await Promise.all(articulos.map((a) => fotoDeArticulo(a.articulos)));
    articulos.forEach((a, i) => { a.foto = fotos[i]; });
  }
  return articulos;
}

export default desgloseOrdenCompra;
